import config from 'Config';
import { getOptimalWidthAndHeight } from './dictionary';

type Format = 'jpeg' | 'webp' | '_AUTOFORMAT_';
export type Transformation = 'crop' | 'resize';

/**
 * Translates the legacy transformation names (`crop`/`resize` from the old
 * path-based API) into the sharp/CSS `fit` values accepted by the new
 * query-param API:
 *   - `crop`   → `cover`  (preserve aspect ratio, fill bounds, crop overflow)
 *   - `resize` → `inside` (preserve aspect ratio, fit within bounds)
 */
const FIT_BY_TRANSFORM: Record<Transformation, 'cover' | 'inside'> = {
  crop: 'cover',
  resize: 'inside',
};

type GetUrl = (arg0: {
  url: string;
  format?: Format;
  width: 'auto' | string | number;
  height: 'auto' | string | number;
  transform: Transformation;
  /** @deprecated gravity is not supported by the new media server */
  gravity?: string;
  quality?: number;
}) => string;

export const getUrl: GetUrl = ({ url, format, width, height, transform, quality }) => {
  if (typeof url !== 'string') {
    return url;
  }

  /*
   * TODO Remove this with issue #14434
   * Due to webedia and media-server incompatibility we need to replace hostname
   * and avoid multiple redirects. See issue #14141.
   */
  const sanitizedUrl = url.replace(
    'http://viaimage2.viafrance.com',
    'http://viaimage3.viafrance.com',
  );

  const optimizedDimensions = getOptimalWidthAndHeight(width, height);

  const params = new URLSearchParams();
  params.set('url', sanitizedUrl);
  // The new media server only accepts pixel values for `width`/`height`; the
  // legacy `auto` sentinel must be omitted so the server auto-scales that axis.
  if (optimizedDimensions.width !== 'auto') {
    params.set('width', String(optimizedDimensions.width));
  }
  if (optimizedDimensions.height !== 'auto') {
    params.set('height', String(optimizedDimensions.height));
  }
  params.set('fit', FIT_BY_TRANSFORM[transform]);
  if (quality != null) {
    params.set('quality', String(quality));
  }
  if (format) {
    // Legacy `_AUTOFORMAT_` sentinel from the old path-based API maps to `webp`
    // on the new query-param API (modern browsers all support webp; the new
    // server does not recognise an `auto` value).
    params.set('format', format === '_AUTOFORMAT_' ? 'webp' : format);
  }

  return `${config.get('media.host')}?${params.toString()}`;
};
